/** WS5 cost-truth helpers: lead with real billed cost, never mix in an estimate. */

import type { UnbilledCloud } from '@lizrd/contracts';

export type CostProvenance = 'billed' | 'estimated';

/** A domain's billed/estimated monthly cost, as `DomainAssessment` reports it. */
export interface DomainCost {
  billedMonthlyCost: number | null;
  projectedBilledCost?: number | null;
  totalMonthlyCost: number;
  /** Cloud accounts on this domain with no billed cost in the total yet. */
  unbilledClouds?: UnbilledCloud[];
}

export interface OrgSpendResult {
  total: number;
  /** Sum of each billed domain's projected full-month billed cost. */
  projectedTotal: number;
  /** 'billed' whenever ANY domain has a real billed figure (the headline is then pure
   * billed and estimate-only accounts are flagged, not summed in); 'estimated' only
   * when nothing is billed anywhere. */
  provenance: CostProvenance;
  /** True when at least one domain contributed a real billed figure. */
  anyBilled: boolean;
  /** Cloud accounts across all domains with no billed cost in the total yet — flagged
   * (never replaced with an estimate) so the customer knows to wire the missing feed. */
  unbilledClouds: UnbilledCloud[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Org-wide monthly spend, billed-primary and NON-mixing: the moment any domain has a
 * real billed figure, the headline is the sum of those billed figures ONLY — estimate-
 * only accounts are excluded and surfaced via `unbilledClouds`, never added in. With no
 * billed signal anywhere, it falls back to the pure estimate.
 */
export function aggregateOrgSpend(domains: DomainCost[]): OrgSpendResult {
  let billedTotal = 0;
  let billedProjected = 0;
  let estimateTotal = 0;
  let anyBilled = false;
  const unbilledClouds: UnbilledCloud[] = [];
  for (const d of domains) {
    if (d.billedMonthlyCost != null && d.billedMonthlyCost > 0) {
      billedTotal += d.billedMonthlyCost;
      billedProjected += d.projectedBilledCost ?? d.billedMonthlyCost;
      anyBilled = true;
    } else if (d.totalMonthlyCost > 0) {
      estimateTotal += d.totalMonthlyCost;
    }
    for (const c of d.unbilledClouds ?? []) unbilledClouds.push(c);
  }
  return {
    total: round2(anyBilled ? billedTotal : estimateTotal),
    projectedTotal: round2(anyBilled ? billedProjected : estimateTotal),
    provenance: anyBilled ? 'billed' : 'estimated',
    anyBilled,
    unbilledClouds,
  };
}
